import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * Teal is the deck's action colour (slides 8-11); amber is reserved for
 * status and award moments, as on the slide-1 approval badge.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-xl font-bold whitespace-nowrap ' +
    'transition-[filter,transform] duration-100 active:scale-[0.99] ' +
    'disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      variant: {
        primary: 'bg-teal text-white shadow-pill hover:brightness-110',
        navy: 'bg-navy-950 text-white shadow-pill hover:brightness-125',
        amber: 'bg-amber text-navy-950 shadow-pill hover:brightness-105',
        outline: 'border border-line bg-white text-navy-950 hover:bg-surface',
        ghost: 'text-navy-950 hover:bg-surface',
        link: 'text-teal underline-offset-4 hover:underline',
      },
      size: {
        // Never below 44px: these are tapped on a phone at a busy event.
        default: 'min-h-11 px-5 py-3 text-[15px]',
        lg: 'min-h-12 px-6 py-3.5 text-base',
        sm: 'min-h-9 px-3 py-2 text-sm',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'default', block: false },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, block }), className)}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
